import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import AddShoppingCartRoundedIcon from "@mui/icons-material/AddShoppingCartRounded";
import LocalDiningRoundedIcon from "@mui/icons-material/LocalDiningRounded";
import { useDashboardTheme } from "../../../context/DashboardThemeContext";
import { useAlert } from "../../../context/AlertContext";

function formatMoney(value) {
  return Number(value || 0).toLocaleString("es-EC", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function round2(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;
}

function round6(value) {
  return Math.round((Number(value || 0) + Number.EPSILON) * 1000000) / 1000000;
}

function normalizeIva(value) {
  const iva = Number(value || 0);
  if (iva === 999) return 999;
  if ([0, 5, 15].includes(iva)) return iva;
  return 0;
}

function getProductFinalPrice(product) {
  return Number(
    product?.precio_final ??
      product?.pvp1 ??
      product?.price ??
      product?.precio ??
      0
  );
}

function getProductIva(product) {
  return normalizeIva(
    product?.iva_porcentaje ??
      product?.iva ??
      product?.iva_percent ??
      0
  );
}

function calculateLineFromFinalPrice(precioConIva, ivaPorcentaje, cantidad) {
  const finalUnitario = Number(precioConIva || 0);
  const qty = Number(cantidad || 0);
  const iva = normalizeIva(ivaPorcentaje);
  const totalFinal = round2(finalUnitario * qty);
  if (iva === 999 || iva <= 0) {
    return {
      precioSinIva: round6(finalUnitario),
      precioConIva: finalUnitario,
      ivaUnitario: 0,
      totalSinIva: totalFinal,
      ivaTotal: 0,
      total: totalFinal,
    };
  }
  const totalSinIvaExacto = totalFinal / (1 + iva / 100);
  const totalSinIvaSri = round2(totalSinIvaExacto);
  const ivaTotalSri = round2(totalFinal - totalSinIvaSri);
  const precioSinIva = qty > 0 ? round6(totalSinIvaExacto / qty) : 0;
  const ivaUnitario = qty > 0 ? round6(ivaTotalSri / qty) : 0;
  return {
    precioSinIva,
    precioConIva: finalUnitario,
    ivaUnitario,
    totalSinIva: totalSinIvaSri,
    ivaTotal: ivaTotalSri,
    total: totalFinal,
  };
}

export default function ModalDetail({
  open,
  onClose,
  product,
  sabores = [],
  frutas = [],
  onAddDetail,
  disabled = false,
}) {
  const { dashboardTheme } = useDashboardTheme();
  const { showError } = useAlert();
  const [cantidad, setCantidad] = useState("1");
  const [observacion, setObservacion] = useState("");
  const [saboresCantidad, setSaboresCantidad] = useState({});
  const [frutasSeleccionadas, setFrutasSeleccionadas] = useState([]);

  useEffect(() => {
    if (open) {
      setCantidad("1");
      setObservacion("");
      setSaboresCantidad({});
      setFrutasSeleccionadas([]);
    }
  }, [open, product?.id]);

  const precioConIva = useMemo(() => getProductFinalPrice(product), [product]);
  const ivaPorcentaje = useMemo(() => getProductIva(product), [product]);
  const calculo = useMemo(
    () => calculateLineFromFinalPrice(precioConIva, ivaPorcentaje, Number(cantidad || 0)),
    [precioConIva, ivaPorcentaje, cantidad]
  );

  const cambiarCantidadSabor = (saborId, delta) => {
    setSaboresCantidad((prev) => {
      const actual = Number(prev[saborId] || 0);
      const siguiente = Math.max(0, actual + delta);
      const nuevo = { ...prev };
      if (siguiente === 0) delete nuevo[saborId];
      else nuevo[saborId] = siguiente;
      return nuevo;
    });
  };

  const toggleFruta = (frutaId) => {
    setFrutasSeleccionadas((prev) =>
      prev.includes(frutaId)
        ? prev.filter((id) => id !== frutaId)
        : [...prev, frutaId]
    );
  };

  const handleAdd = () => {
    if (!product?.id) {
      showError("No hay un producto seleccionado.");
      return;
    }
    const cantidadNumber = Number(cantidad || 0);
    if (cantidadNumber <= 0) {
      showError("La cantidad debe ser mayor a cero.");
      return;
    }
    const saboresDetalle = sabores
      .filter((sabor) => Number(saboresCantidad[sabor.id] || 0) > 0)
      .map((sabor) => ({
        id: sabor.id,
        sabor: sabor.id,
        sabor_id: sabor.id,
        sabor_nombre: sabor.nombre,
        nombre: sabor.nombre,
        cantidad: Number(saboresCantidad[sabor.id]),
      }));
    const frutasDetalle = frutas
      .filter((fruta) => frutasSeleccionadas.includes(fruta.id))
      .map((fruta) => ({
        id: fruta.id,
        fruta: fruta.id,
        fruta_id: fruta.id,
        fruta_nombre: fruta.nombre,
        nombre: fruta.nombre,
      }));
    onAddDetail({
      uid: `${product.id}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      producto: product.id,
      producto_id: product.id,
      producto_nombre: product.name || product.nombre || "",
      descripcion: product.name || product.nombre || "",
      cantidad: cantidadNumber,
      precio_unitario: calculo.precioSinIva,
      precio_sin_iva: calculo.precioSinIva,
      precio_con_iva: calculo.precioConIva,
      iva_porcentaje: ivaPorcentaje,
      iva_unitario: calculo.ivaUnitario,
      total_sin_iva: calculo.totalSinIva,
      iva_total: calculo.ivaTotal,
      total: calculo.total,
      observacion: observacion.trim(),
      sabores: saboresDetalle,
      frutas: frutasDetalle,
      image: product.image || "",
    });
    onClose();
  };

  const outlinedButtonSx = {
    minHeight: 38,
    px: 2,
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    color: dashboardTheme.button_color,
    borderColor: dashboardTheme.button_color,
    boxShadow: "none",
    "&:hover": {
      borderColor: dashboardTheme.button_color,
      bgcolor: `${dashboardTheme.button_color}12`,
      boxShadow: "none",
    },
  };

  return (
    <Dialog
      open={open}
      onClose={disabled ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: "18px",
          bgcolor: dashboardTheme.card_background,
          border: `1px solid ${dashboardTheme.border_color}`,
          boxShadow: "none",
          maxHeight: "90vh",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 700,
          fontSize: 20,
          color: dashboardTheme.primary_color,
          pb: 1.5,
        }}
      >
        Configurar producto
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: dashboardTheme.border_color }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "90px minmax(0,1fr)" },
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              width: { xs: 90, sm: 90 },
              height: 90,
              borderRadius: "14px",
              border: `1px solid ${dashboardTheme.border_color}`,
              display: "grid",
              placeItems: "center",
              overflow: "hidden",
              bgcolor:
                dashboardTheme.theme_mode === "dark"
                  ? "rgba(255,255,255,0.04)"
                  : "rgba(17,24,39,0.04)",
            }}
          >
            {product?.image ? (
              <Box
                component="img"
                src={product.image}
                alt={product?.name || product?.nombre || ""}
                sx={{ width: "100%", height: "100%", objectFit: "contain", p: 1 }}
              />
            ) : (
              <LocalDiningRoundedIcon
                sx={{ fontSize: 42, color: dashboardTheme.muted_text_color }}
              />
            )}
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: 19,
                fontWeight: 700,
                color: dashboardTheme.text_color,
                lineHeight: 1.2,
              }}
            >
              {product?.name || product?.nombre || "-"}
            </Typography>
            <Typography
              sx={{
                mt: 0.6,
                fontSize: 16,
                fontWeight: 700,
                color: dashboardTheme.primary_color,
              }}
            >
              ${formatMoney(precioConIva)}
            </Typography>
            {(product?.description || product?.descripcion) && (
              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 13,
                  color: dashboardTheme.muted_text_color,
                }}
              >
                {product.description || product.descripcion}
              </Typography>
            )}
          </Box>
        </Box>
        <Divider sx={{ my: 2, borderColor: dashboardTheme.border_color }} />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <TextField
            size="small"
            type="number"
            label="Cantidad"
            value={cantidad}
            onChange={(e) => setCantidad(e.target.value)}
            inputProps={{ min: 1 }}
            disabled={disabled}
            sx={{ width: { xs: "100%", sm: 150 }, ...dashboardTheme.inputStyles }}
          />
          <TextField
            fullWidth
            size="small"
            label="Detalle adicional"
            placeholder="Ej: sin chocolate, con galleta"
            value={observacion}
            onChange={(e) => setObservacion(e.target.value)}
            disabled={disabled}
            sx={dashboardTheme.inputStyles}
          />
        </Stack>
        <Divider sx={{ my: 2, borderColor: dashboardTheme.border_color }} />
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: dashboardTheme.text_color,
            mb: 1.2,
          }}
        >
          Sabores
        </Typography>
        {sabores.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: dashboardTheme.muted_text_color }}>
            No hay sabores activos.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2,minmax(0,1fr))",
                md: "repeat(3,minmax(0,1fr))",
              },
              gap: 1,
            }}
          >
            {sabores.map((sabor) => {
              const valor = Number(saboresCantidad[sabor.id] || 0);
              return (
                <Box
                  key={sabor.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 1,
                    p: 1,
                    borderRadius: "12px",
                    border: `1px solid ${dashboardTheme.border_color}`,
                    bgcolor: valor > 0 ? `${dashboardTheme.primary_color}0D` : "transparent",
                  }}
                >
                  <Typography
                    sx={{
                      minWidth: 0,
                      fontSize: 14,
                      fontWeight: valor > 0 ? 700 : 500,
                      color: dashboardTheme.text_color,
                    }}
                  >
                    {sabor.nombre}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.4}>
                    <IconButton
                      size="small"
                      disabled={disabled || valor <= 0}
                      onClick={() => cambiarCantidadSabor(sabor.id, -1)}
                      sx={{
                        border: `1px solid ${dashboardTheme.border_color}`,
                        borderRadius: "8px",
                      }}
                    >
                      <RemoveRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                    <Typography
                      sx={{
                        width: 26,
                        textAlign: "center",
                        fontWeight: 700,
                        color: dashboardTheme.text_color,
                      }}
                    >
                      {valor}
                    </Typography>
                    <IconButton
                      size="small"
                      disabled={disabled}
                      onClick={() => cambiarCantidadSabor(sabor.id, 1)}
                      sx={{
                        border: `1px solid ${dashboardTheme.border_color}`,
                        borderRadius: "8px",
                      }}
                    >
                      <AddRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}
        <Divider sx={{ my: 2, borderColor: dashboardTheme.border_color }} />
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: dashboardTheme.text_color,
            mb: 1.2,
          }}
        >
          Frutas
        </Typography>
        {frutas.length === 0 ? (
          <Typography sx={{ fontSize: 14, color: dashboardTheme.muted_text_color }}>
            No hay frutas activas.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2,minmax(0,1fr))",
                sm: "repeat(3,minmax(0,1fr))",
                md: "repeat(4,minmax(0,1fr))",
              },
              gap: 0.8,
            }}
          >
            {frutas.map((fruta) => {
              const checked = frutasSeleccionadas.includes(fruta.id);
              return (
                <Box
                  key={fruta.id}
                  onClick={() => !disabled && toggleFruta(fruta.id)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.4,
                    px: 0.8,
                    py: 0.5,
                    borderRadius: "10px",
                    border: `1px solid ${dashboardTheme.border_color}`,
                    cursor: disabled ? "default" : "pointer",
                    bgcolor: checked ? `${dashboardTheme.primary_color}0D` : "transparent",
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={checked}
                    disabled={disabled}
                    onChange={() => toggleFruta(fruta.id)}
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      p: 0.5,
                      color: dashboardTheme.muted_text_color,
                      "&.Mui-checked": { color: dashboardTheme.primary_color },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: 14,
                      fontWeight: checked ? 700 : 500,
                      color: dashboardTheme.text_color,
                    }}
                  >
                    {fruta.nombre}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
        <Box
          sx={{
            mt: 2,
            p: 1.5,
            borderRadius: "12px",
            border: `1px solid ${dashboardTheme.border_color}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography sx={{ fontWeight: 600, color: dashboardTheme.text_color }}>
            Total
          </Typography>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 700,
              color: dashboardTheme.primary_color,
            }}
          >
            ${formatMoney(calculo.total)}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Button variant="outlined" onClick={onClose} disabled={disabled} sx={outlinedButtonSx}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          startIcon={<AddShoppingCartRoundedIcon />}
          onClick={handleAdd}
          disabled={disabled}
          sx={dashboardTheme.buttonStyles.contained}
        >
          Añadir a la orden
        </Button>
      </DialogActions>
    </Dialog>
  );
}